import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { AuthAction } from '../../actions/AuthAction';
import { Translate as t } from '../../config/Translate';
import { Loading } from '../common/Loading';
import { needsIubendaConsent } from '../../lib/loginLanding';
import { loadConsentText, TEXT_FORMAT_HTML } from '../../lib/consentText';
import { submitConsent, newAttemptId } from '../../lib/consentService';
import { safeGetJSON } from '../../lib/safeStorage';
import ConsentModal from './ConsentModal';

// FEAT-052 (#493): 동의 게이트 — 보호 화면과 모달 중 **하나만** 렌더한다.
//
// 🔴 **모달을 보호 화면 위에 얹지 않는다.** 얹으면 뒤 화면이 실제로 마운트되어 effect 가 돌고
// 데이터를 받아온다 — 모달은 가려줄 뿐 막지 못한다. 종전 `/IubendaLogin` 리다이렉트는 보호
// 화면을 아예 렌더하지 않았으므로, 같은 차단 강도를 유지하려면 게이트가 열릴 때까지 보호
// 컴포넌트를 **만들지도 말아야** 한다. 그래서 children 을 함수로 받는다 — 게이트가 닫혀 있는
// 동안에는 호출되지 않으니 React element 조차 생기지 않는다.
//
// 판정 SSoT 는 needsIubendaConsent(loginLanding.js). AuthRoute 는 localStorage 를, 이 게이트는
// redux 를 읽는데 둘은 항상 일치한다 — AuthReducer 의 initialState 가 localStorage 에서
// 하이드레이트되고, UpdateUser 는 **저장소 기록에 성공한 경우에만** redux 에 반영한다
// (실패 시 양쪽 모두 미반영 = fail-closed).
//
// 왜 redux 를 구독하는가 — 동의 성공 뒤 리로드 없이 화면이 열려야 하기 때문이다. AuthRoute 는
// 자기 render prop 안에서 이미 판정을 끝냈으므로, 조상이 다시 렌더되지 않으면 그 판정이 갱신될
// 길이 없다. 게이트가 직접 redux 를 구독하면 UPDATE_SUCCESS 한 번으로 스스로 열린다.

export const ConsentGate = ({ user, UpdateUser, logout, children }) => {
    // policy: null = 조회 중, { text, version, locale } = 성공, { error: true } = 실패
    const [policy, setPolicy] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const aliveRef = useRef(true);
    // 「한 번의 동의 행위」를 가리키는 값. **모달 1회 열림마다 하나**이고 재시도해도 안 바뀐다.
    // 멱등 키의 일부가 되어, 같은 모달 안의 재시도는 중복을 안 만들고 새 모달은 실제로 쌓인다.
    // useRef 라 재렌더로 바뀌지 않는다 — useState 초기값으로 두면 읽기는 같지만 의도가 흐려진다.
    const attemptIdRef = useRef(null);
    if (attemptIdRef.current === null) attemptIdRef.current = newAttemptId();

    useEffect(() => () => { aliveRef.current = false; }, []);

    const fetchPolicy = useCallback(async () => {
        setPolicy(null);
        setErrorMessage('');
        // loadConsentText 는 throw 하지 않는다(실패도 { error: true } 로 돌려준다).
        const res = await loadConsentText();
        if (!aliveRef.current) return;
        setPolicy(res);
        if (res?.error) setErrorMessage(t('consent.loadFailed'));
    }, []);

    const consentPending = needsIubendaConsent(user);

    useEffect(() => {
        if (consentPending) fetchPolicy();
    }, [consentPending, fetchPolicy]);

    const handleAgree = async () => {
        if (submitting) return;
        const version = policy?.version;
        const locale = policy?.locale;
        if (!version || !locale) {
            // 여기 도달하면 canSubmit 가드가 샌 것이다 — 조용히 넘기지 않는다.
            console.warn('[ConsentGate] agree blocked — policy version/locale missing', {
                handler: 'ConsentGate.handleAgree',
                hasVersion: !!version,
                hasLocale: !!locale,
                reason: 'record would not point at a specific policy version',
            });
            setErrorMessage(t('consent.loadFailed'));
            return;
        }

        setSubmitting(true);
        setErrorMessage('');
        try {
            // 순서를 바꾸지 말 것 — **기록이 먼저, 플래그가 나중**이다.
            // 반대로 하면 isIubenda=true 인데 동의 기록이 없는 계정이 생기고, 그 사람은 다시는
            // 모달을 보지 않으므로 기록이 영영 비는 상태로 굳는다. 지금 순서라면 최악의 경우
            // "기록은 있는데 플래그가 없는" 상태가 되고, 다음 로그인 때 한 번 더 동의를 받는다
            // — RAYConsent 는 중복 적재를 오류로 보지 않는다(멱등 재전송은 200 duplicate).
            const res = await submitConsent({
                textVersion: version,
                locale,
                granted: true,
                attemptId: attemptIdRef.current,
            });
            if (!res?.ok) {
                setErrorMessage(t('consent.submitFailed'));
                return;
            }
            // 저장 성공 시 UpdateUser 가 localStorage 와 redux 를 함께 갱신한다 →
            // 이 컴포넌트가 재렌더되며 게이트가 열린다(리다이렉트·리로드 없음).
            // isIubendaId(구 iubenda subject_id)는 건드리지 않는다 — 새 구조의 subject_id 는
            // BE 가 사용자 _id 에서 파생하므로 보관할 값이 없고, 기존 필드 삭제는 금지다.
            await UpdateUser({ isIubenda: true });
            // UpdateUser 는 실패해도 throw 하지 않고 전역 토스트만 띄운다. 성공이면 저장소가
            // 먼저 갱신되므로 저장소로 판정한다(렌더 시점과 무관). 기록은 이미 남았으므로
            // 재클릭은 같은 attemptId → duplicate 로 안전하다.
            if (aliveRef.current && needsIubendaConsent(safeGetJSON('user'))) {
                console.warn('[ConsentGate] consent recorded but isIubenda flag not saved', {
                    handler: 'ConsentGate.handleAgree',
                    textVersion: version,
                    reason: 'UpdateUser failed or storage blocked — user stays at the modal',
                });
                setErrorMessage(t('consent.submitFailed'));
            }
        } finally {
            if (aliveRef.current) setSubmitting(false);
        }
    };

    // 동의 화면의 유일한 탈출구(F-004). 적재가 실패해 모달에 갇히더라도 반드시 나갈 수 있어야
    // 한다 — 전송 중일 때만 막는다.
    // 거부 자체를 granted:false 로 적재할지는 미정이다(#493 §7-1). 지금은 종전 /IubendaLogin
    // 화면과 동일하게 로그아웃만 한다.
    const handleDecline = () => {
        if (submitting) return;
        logout();
    };

    if (!consentPending) {
        return typeof children === 'function' ? children() : (children || null);
    }

    // 문구를 받아오는 동안에도 보호 화면은 렌더하지 않는다.
    if (policy === null) return <Loading />;

    const failed = !!policy?.error;
    // 형식은 RAYConsent 가 그 판과 함께 선언한 값이다 — 우리가 추측하지 않는다.
    const asHtml = !failed && policy.format === TEXT_FORMAT_HTML;

    return (
        <ConsentModal
            bodyHtml={asHtml ? policy.text : ''}
            bodyText={failed || asHtml ? '' : policy.text}
            version={failed ? '' : policy.version}
            submitting={submitting}
            canSubmit={!failed}
            onRetry={failed ? fetchPolicy : undefined}
            errorMessage={errorMessage}
            onAgree={handleAgree}
            onDecline={handleDecline}
        />
    );
};

const mapState = (state) => ({
    user: state.AuthReducer.user,
});

const mapDispatch = (dispatch) => ({
    UpdateUser: (userInfo) => dispatch(AuthAction.UpdateUser(userInfo)),
    logout: () => dispatch(AuthAction.Logout()),
});

export default connect(mapState, mapDispatch)(ConsentGate);
